/**
 * SmsGatewayWizard — register an organisation's own SMS gateway.
 *
 * The steps exist because a number cannot be claimed by typing it: the
 * organisation must store its key, prove the phone is on that account, and
 * prove the SIM receives a code. Only then does the gateway go live.
 */
import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { CheckCircle2, Loader2, ShieldCheck, Smartphone } from 'lucide-react';
import { smsAPI, churchesAPI, getErrorMessage } from '@/lib/api';

const PURPOSES = ['service', 'transactional', 'operational', 'pastoral', 'marketing'];

const Step = ({ n, current, title, children }) => (
  <div className={`border rounded-xl p-5 ${current === n ? 'border-slate-900 bg-white' : 'border-slate-200 bg-slate-50/60'}`}>
    <p className="text-xs uppercase tracking-[0.16em] text-slate-400">Step {n}</p>
    <h3 className="font-semibold mt-1 mb-3 text-slate-900">{title}</h3>
    {current === n && <div className="space-y-3">{children}</div>}
  </div>
);

export const SmsGatewayWizard = ({ onDone }) => {
  const qc = useQueryClient();
  const [step, setStep] = useState(1);
  const [gateway, setGateway] = useState(null);
  const [form, setForm] = useState({
    organisation_id: '', country: '', name: '', sending_number: '',
    provider: 'sms8', account_mode: 'own_account', network_operator: '',
    currency: '', estimated_cost_per_segment: 0, test_mode: true,
    allowed_purposes: ['service', 'transactional', 'operational'],
  });
  const [apiKey, setApiKey] = useState('');
  const [webhookSecret, setWebhookSecret] = useState('');
  const [device, setDevice] = useState({ device_id: '', sim_slot: '' });
  const [code, setCode] = useState('');
  const [testCode, setTestCode] = useState('');

  const { data: orgs = [] } = useQuery({
    queryKey: ['sms-wizard-orgs'],
    queryFn: () => churchesAPI.list().then((r) => (Array.isArray(r.data) ? r.data : r.data?.items || [])),
  });

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const fail = (e, fallback) => toast.error(getErrorMessage(e, fallback));

  const create = useMutation({
    mutationFn: () => smsAPI.createGateway({
      ...form,
      estimated_cost_per_segment: Number(form.estimated_cost_per_segment) || 0,
    }),
    onSuccess: (r) => { setGateway(r.data); setStep(4); toast.success('Gateway registered'); },
    onError: (e) => fail(e, 'Could not register the gateway'),
  });
  const saveKey = useMutation({
    mutationFn: () => smsAPI.saveCredentials(gateway.id, {
      api_key: apiKey || undefined, webhook_secret: webhookSecret || undefined }),
    onSuccess: () => { toast.success('Credentials stored, encrypted'); setStep(5); },
    onError: (e) => fail(e, 'Could not store the credentials'),
  });
  const pair = useMutation({
    mutationFn: () => smsAPI.pair(gateway.id, {
      device_id: device.device_id, sim_slot: device.sim_slot || undefined }),
    onSuccess: () => { toast.success('Device paired'); setStep(6); },
    onError: (e) => fail(e, 'Pairing failed'),
  });
  const startVerify = useMutation({
    mutationFn: () => smsAPI.startVerify(gateway.id),
    onSuccess: (r) => {
      setTestCode(r.data?.test_code || '');
      toast.success(r.data?.test_code
        ? `Test mode — your code is ${r.data.test_code}`
        : `Code sent to ${r.data?.sent_to}`);
    },
    onError: (e) => fail(e, 'Could not send the verification code'),
  });
  const confirm = useMutation({
    mutationFn: () => smsAPI.confirmVerify(gateway.id, code),
    onSuccess: () => {
      toast.success('Number verified — the gateway is active');
      qc.invalidateQueries({ queryKey: ['sms-gateways'] });
      onDone?.();
    },
    onError: (e) => fail(e, 'That code was not accepted'),
  });

  return (
    <div className="space-y-4" data-testid="sms-gateway-wizard">
      <Card>
        <CardHeader><CardTitle className="text-lg">Register an SMS gateway</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <Step n={1} current={step} title="Which organisation owns it?">
            <Select value={form.organisation_id}
                    onValueChange={(v) => {
                      const org = orgs.find((o) => o.id === v) || {};
                      set({ organisation_id: v, country: org.country || form.country,
                            name: org.name ? `${org.name} SMS` : form.name });
                    }}>
              <SelectTrigger data-testid="wizard-org-select">
                <SelectValue placeholder="Select your organisation" />
              </SelectTrigger>
              <SelectContent>
                {orgs.map((o) => (
                  <SelectItem key={o.id} value={o.id}>
                    {o.name}{o.country ? ` · ${o.country}` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-slate-500">
              Only organisations you administer are listed, and the server checks it again.
            </p>
            <Button disabled={!form.organisation_id} onClick={() => setStep(2)}
                    data-testid="wizard-step1-next">Continue</Button>
          </Step>

          <Step n={2} current={step} title="Country, gateway type and bill payer">
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label>Country</Label>
                <Input value={form.country} onChange={(e) => set({ country: e.target.value })}
                       placeholder="e.g. Zimbabwe" data-testid="wizard-country" />
              </div>
              <div>
                <Label>Currency</Label>
                <Input value={form.currency} onChange={(e) => set({ currency: e.target.value })}
                       placeholder="USD / GBP / PLN" data-testid="wizard-currency" />
              </div>
            </div>
            <div>
              <Label>Gateway type</Label>
              <Select value={form.account_mode} onValueChange={(v) => set({ account_mode: v })}>
                <SelectTrigger data-testid="wizard-account-mode"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="own_account">
                    Our own SMS8 account, our own Android phone and SIM — we pay our network
                  </SelectItem>
                  <SelectItem value="central_account">
                    Our phone paired inside the FWM SMS8 account — FWM holds the plan
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={() => setStep(3)} disabled={!form.country}
                    data-testid="wizard-step2-next">Continue</Button>
          </Step>

          <Step n={3} current={step} title="The sending number">
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label>SIM number (E.164)</Label>
                <Input value={form.sending_number} placeholder="+263771234567"
                       onChange={(e) => set({ sending_number: e.target.value.trim() })}
                       data-testid="wizard-number" />
              </div>
              <div>
                <Label>Mobile network</Label>
                <Input value={form.network_operator} placeholder="Econet / EE / Orange"
                       onChange={(e) => set({ network_operator: e.target.value })}
                       data-testid="wizard-operator" />
              </div>
            </div>
            <div className="flex items-center justify-between rounded-lg border p-3">
              <div>
                <p className="text-sm font-medium">Test mode</p>
                <p className="text-xs text-slate-500">
                  Nothing leaves the server. Turn it off once a real SIM is paired.
                </p>
              </div>
              <Switch checked={form.test_mode} onCheckedChange={(v) => set({ test_mode: v })}
                      data-testid="wizard-test-mode" />
            </div>
            <Button onClick={() => create.mutate()}
                    disabled={!/^\+[1-9]\d{6,14}$/.test(form.sending_number) || create.isPending}
                    data-testid="wizard-register-btn">
              {create.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Register gateway
            </Button>
          </Step>

          <Step n={4} current={step} title="Store the SMS8 credentials">
            <div>
              <Label>SMS8 API key</Label>
              <Input value={apiKey} onChange={(e) => setApiKey(e.target.value)}
                     placeholder="from app.sms8.io → Profile → API" type="password"
                     data-testid="wizard-api-key" />
            </div>
            <div>
              <Label>Webhook signing secret (optional, for replies)</Label>
              <Input value={webhookSecret} onChange={(e) => setWebhookSecret(e.target.value)}
                     type="password" data-testid="wizard-webhook-secret" />
            </div>
            <p className="text-xs text-slate-500">
              Encrypted at rest. Only the last four characters are ever shown again.
            </p>
            <div className="flex gap-2">
              <Button onClick={() => saveKey.mutate()} disabled={!apiKey || saveKey.isPending}
                      data-testid="wizard-save-key">Store and continue</Button>
              {form.test_mode && (
                <Button variant="outline" onClick={() => setStep(5)}
                        data-testid="wizard-skip-key">Skip — test mode</Button>
              )}
            </div>
          </Step>

          <Step n={5} current={step} title="Pair the phone">
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label>SMS8 device ID</Label>
                <Input value={device.device_id} data-testid="wizard-device-id"
                       onChange={(e) => setDevice({ ...device, device_id: e.target.value.trim() })} />
              </div>
              <div>
                <Label>SIM slot (0 or 1)</Label>
                <Input value={device.sim_slot} data-testid="wizard-sim-slot"
                       onChange={(e) => setDevice({ ...device, sim_slot: e.target.value.trim() })} />
              </div>
            </div>
            <p className="text-xs text-slate-500 flex items-start gap-2">
              <Smartphone className="h-3.5 w-3.5 mt-0.5" />
              We ask SMS8 which phones your key owns. A device another organisation
              already holds is refused.
            </p>
            <Button onClick={() => pair.mutate()} disabled={!device.device_id || pair.isPending}
                    data-testid="wizard-pair-btn">
              {pair.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Pair device
            </Button>
          </Step>

          <Step n={6} current={step} title="Verify the number, then go live">
            <Button variant="outline" onClick={() => startVerify.mutate()}
                    disabled={startVerify.isPending} data-testid="wizard-send-code">
              {startVerify.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Send verification code to {form.sending_number}
            </Button>
            {testCode && (
              <p className="text-xs text-amber-700" data-testid="wizard-test-code">
                Test mode code: <strong>{testCode}</strong>
              </p>
            )}
            <div>
              <Label>Code</Label>
              <Input value={code} onChange={(e) => setCode(e.target.value.trim())}
                     maxLength={6} data-testid="wizard-code" />
            </div>
            <div>
              <Label>Approved message types</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {PURPOSES.map((p) => {
                  const on = form.allowed_purposes.includes(p);
                  return (
                    <button key={p} type="button"
                            data-testid={`wizard-purpose-${p}`}
                            onClick={() => set({ allowed_purposes: on
                              ? form.allowed_purposes.filter((x) => x !== p)
                              : [...form.allowed_purposes, p] })}
                            className={`px-3 py-1.5 rounded-full text-xs border capitalize transition-colors ${
                              on ? 'bg-slate-900 text-white border-slate-900'
                                 : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'}`}>
                      {p}
                    </button>
                  );
                })}
              </div>
            </div>
            <Button onClick={async () => {
              await smsAPI.updateGateway(gateway.id, { allowed_purposes: form.allowed_purposes })
                .catch(() => {});
              confirm.mutate();
            }} disabled={code.length < 4 || confirm.isPending} data-testid="wizard-activate">
              {confirm.isPending
                ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                : <ShieldCheck className="h-4 w-4 mr-2" />}
              Verify and activate
            </Button>
          </Step>

          {step > 3 && gateway && (
            <p className="text-xs text-slate-500 flex items-center gap-2">
              <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
              {gateway.name} · {gateway.sending_number} · {gateway.country}
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SmsGatewayWizard;
